import { useMemo, useState } from 'react';
import { ITrainingCategory } from '../../../models/allenamenti';
import { useCategoriesManager } from '../hooks/useCategoriesManager';
import { CATEGORY_SWATCHES, filterCategories } from './categoriesModal.constants';

/**
 * Che cosa sta facendo il form, e DOVE.
 *
 * Il punto è `parentSlug`: non è un campo che l'utente compila, è il posto da
 * cui ha premuto «+». Prima era una tendina «Posizione» in cima alla modal, che
 * chiedeva di dichiarare la gerarchia in astratto mentre la gerarchia stessa era
 * disegnata lì sotto.
 */
export type TCategoriesMode =
  | { kind: 'idle' }
  | { kind: 'create'; parentSlug: string | null }
  | { kind: 'edit'; slug: string; parentSlug: string | null };

export interface ICategoryFormDraft {
  labelIt: string;
  color: string;
  isPhysical: boolean;
  /** Gruppi di campi extra abilitati sul form blocco (solo macro, ADR 029). */
  fieldsEnabled: string[];
}

const EMPTY: ICategoryFormDraft = {
  labelIt: '',
  color: CATEGORY_SWATCHES[5],
  isPhysical: false,
  fieldsEnabled: [],
};

/** Un nome sotto le due lettere non identifica niente e lo slug diventa muto. */
const MIN_LABEL = 2;

/**
 * Stato di interfaccia della modal categorie: dove è aperto il form, che cosa
 * contiene, quale riga sta chiedendo conferma di eliminazione, che cosa è
 * filtrato. Le chiamate di rete restano in `useCategoriesManager`.
 */
export const useCategoriesModal = (categories: ITrainingCategory[]) => {
  const { create, update, remove, reorder, error, clearError, isSaving } =
    useCategoriesManager();

  const [mode, setMode] = useState<TCategoriesMode>({ kind: 'idle' });
  const [draft, setDraft] = useState<ICategoryFormDraft>(EMPTY);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  /**
   * Le macro aperte. Di default NESSUNA: le dodici macro stanno in una
   * schermata, le ventisei righe di tutto aperto no — ed era il motivo per cui
   * questa lista si scorreva tanto.
   */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(
    () => filterCategories(categories, query),
    [categories, query],
  );
  const isSearching = query.trim().length > 0;
  const visible = filtered;

  /**
   * Cercando, i gruppi con un risultato si aprono da soli: un elenco di macro
   * chiuse in risposta a una ricerca non mostrerebbe ciò che si è cercato.
   */
  const isExpanded = (slug: string) => isSearching || expanded.has(slug);

  const toggle = (slug: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const close = () => {
    setMode({ kind: 'idle' });
    setDraft(EMPTY);
    clearError();
  };

  const startCreate = (parentSlug: string | null, inheritedColor?: string) => {
    clearError();
    setPendingDelete(null);
    setMode({ kind: 'create', parentSlug });
    // Una sottocategoria non ha colore proprio: nel calendario porta quello
    // della macro. Ereditarlo qui evita di mostrare un controllo che non
    // produce alcun effetto visibile.
    setDraft({ ...EMPTY, color: inheritedColor ?? EMPTY.color });
  };

  const startEdit = (
    slug: string,
    parentSlug: string | null,
    values: ICategoryFormDraft,
  ) => {
    clearError();
    setPendingDelete(null);
    setMode({ kind: 'edit', slug, parentSlug });
    setDraft(values);
  };

  /**
   * Riordina trascinando: porta `slug` nella posizione di `overSlug`.
   *
   * Vale sia fra macro sia fra sotto della stessa macro; se le due voci non
   * vivono nella stessa lista non si fa nulla, perché trascinare una sotto
   * fuori dal suo gruppo significherebbe cambiarle il parent, ed è un'altra
   * azione (la tendina «Dentro» nel form).
   *
   * Si manda la sequenza COMPLETA e non "sposta di N": la scrittura resta
   * idempotente e due trascinamenti ravvicinati non si sommano in modo
   * imprevedibile.
   */
  const reorderTo = (slug: string, overSlug: string) => {
    /** La lista in cui vive uno slug: le macro, oppure le sotto di una macro. */
    const listaDi = (s: string) => {
      if (categories.some((c) => c.slug === s)) {
        return { parentSlug: null as string | null, voci: categories };
      }
      const parent = categories.find((c) =>
        c.subcategories.some((x) => x.slug === s),
      );
      return parent
        ? { parentSlug: parent.slug, voci: parent.subcategories }
        : null;
    };

    const a = listaDi(slug);
    const b = listaDi(overSlug);
    if (!a || !b || a.parentSlug !== b.parentSlug) return;

    const slugs = a.voci.map((c) => c.slug);
    const from = slugs.indexOf(slug);
    const to = slugs.indexOf(overSlug);
    if (from < 0 || to < 0 || from === to) return;

    slugs.splice(to, 0, slugs.splice(from, 1)[0]);

    // Come sarà la lista: serve all'anticipo, perché il trascinamento deve
    // concludersi dove il dito ha lasciato la riga, non mezzo secondo dopo.
    const riordinate = [...a.voci];
    riordinate.splice(to, 0, riordinate.splice(from, 1)[0]);

    const optimistic = a.parentSlug
      ? categories.map((c) =>
          c.slug === a.parentSlug
            ? { ...c, subcategories: riordinate as typeof c.subcategories }
            : c,
        )
      : (riordinate as typeof categories);

    const body = a.parentSlug
      ? { subOrder: { [a.parentSlug]: slugs } }
      : { macroOrder: slugs };

    reorder(body, optimistic).catch(() => {
      // `useCategoriesManager` rimette la lista di prima e riempie il banner.
    });
  };

  /** Sposta una voce sotto un'altra macro, o fuori da tutte (`null`). */
  const moveTo = async (slug: string, parentSlug: string | null) => {
    try {
      await update(slug, { parentSlug });
      close();
    } catch {
      // Il 409 «contiene sottocategorie» e il 400 «parent non valido» finiscono
      // nel banner: il form resta aperto con dentro quello che si è scritto.
    }
  };

  const setField = <K extends keyof ICategoryFormDraft>(
    key: K,
    value: ICategoryFormDraft[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const isValid = draft.labelIt.trim().length >= MIN_LABEL;

  const submit = async () => {
    if (!isValid || isSaving || mode.kind === 'idle') return;
    try {
      if (mode.kind === 'edit') {
        await update(mode.slug, {
          labelIt: draft.labelIt.trim(),
          color: draft.color,
          isPhysical: draft.isPhysical,
          // Solo le macro portano i gruppi campi: sulle sotto è ignorato.
          ...(mode.parentSlug === null && { fieldsEnabled: draft.fieldsEnabled }),
        });
      } else {
        await create({
          labelIt: draft.labelIt.trim(),
          color: draft.color,
          parentSlug: mode.parentSlug ?? '',
          isPhysical: draft.isPhysical,
          ...(mode.parentSlug === null && { fieldsEnabled: draft.fieldsEnabled }),
        });
        /*
          Si apre il gruppo in cui la voce è appena nata. Ad averlo lasciato
          chiuso, il salvataggio riusciva e a schermo non cambiava nulla: la
          voce c'era ma dentro qualcosa di richiuso.

          Qui e non al clic sul «+»: aprire un gruppo per un form poi annullato
          sarebbe un effetto senza causa.
        */
        if (mode.parentSlug) {
          const parent = mode.parentSlug;
          setExpanded((prev) => new Set(prev).add(parent));
        }
      }
      close();
    } catch {
      // L'errore è già nello stato di `useCategoriesManager`: il form resta
      // compilato e aperto, così quello che si è scritto non va perso.
    }
  };

  const confirmDelete = async (slug: string) => {
    try {
      await remove(slug);
      setPendingDelete(null);
      if (mode.kind === 'edit' && mode.slug === slug) close();
    } catch {
      // Il 409 «categoria in uso» finisce nel banner e dice quanti blocchi la
      // usano: la riga resta in stato di conferma, così il messaggio è
      // leggibile accanto alla riga a cui si riferisce.
    }
  };

  return {
    mode,
    draft,
    isValid,
    isSaving,
    error,
    query,
    visible,
    pendingDelete,
    isExpanded,
    toggle,
    reorderTo,
    moveTo,
    setQuery,
    setField,
    startCreate,
    startEdit,
    close,
    submit,
    askDelete: (slug: string | null) => {
      clearError();
      setPendingDelete(slug);
    },
    confirmDelete,
  };
};
